import { GetStaticProps } from 'next';
import { Router } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useWindowSize } from 'usehooks-ts';

import SEO from 'components/SEO/SEO';
import { useCopyStore, useGlobalStore } from 'store';
import { CopyStoreType } from 'store/copy.types';
import { appState } from 'utils/appState';
import { queryCMS } from 'utils/cms/api';
import { contactPageQuery } from 'utils/cms/gql';
import { ISR_TIMEOUT } from 'utils/config';
import { ROUTES } from 'utils/routes';
import { marketPageId, MarketPages } from 'utils/sharedStrapiQueries/market';
import { pageMotionProps } from 'utils/styles/animations';
import { breakpointDesktop } from 'utils/styles/vars';

import { ContactPageDataCMS, DataCMSTypes } from './ContactPage.types';

import * as S from './ContactPage.styles';

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const id = await marketPageId(MarketPages.contactPage, locale);
  const cookieId = await marketPageId(MarketPages.cookieBar, locale);
  const footerId = await marketPageId(MarketPages.footer, locale);

  const globalIds = { cookieId, footerId };

  const response = await queryCMS<DataCMSTypes>({
    query: contactPageQuery({ id, locale, globalIds }),
  });

  const {
    data: {
      sharedCopies,
      contactPage: page,
      redirectPopups,
      cookieBar,
      navbar,
      footer,
    },
  } = response;

  if (!page || !sharedCopies) return { notFound: true };

  return {
    props: {
      initialCopy: {
        head: page?.seo || null,
        global: sharedCopies[0]?.global || null,
        globalSettings: sharedCopies[0] || null,
        redirectPopup: redirectPopups[0] || null,
        cookieBar: cookieBar || null,
        navbar,
        footer,
      },
      page,
    },
    revalidate: ISR_TIMEOUT,
  };
};

interface ContactPageProps {
  initialCopy: {
    head: CopyStoreType['copy']['head'];
    global: CopyStoreType['copy']['global'];
    globalSettings: CopyStoreType['copy']['globalSettings'];
    redirectPopup: CopyStoreType['copy']['redirectPopup'];
    navbar: CopyStoreType['copy']['navbar'];
    footer: CopyStoreType['copy']['footer'];
  };
  page: ContactPageDataCMS;
  router: Router;
}

const ContactPage: React.FunctionComponent<ContactPageProps> = ({
  initialCopy,
  page,
}) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isDesktop, setIsDesktop] = useState<boolean | null>(null);

  const { width } = useWindowSize();

  const { setCopy } = useCopyStore();
  const { setHeaderText } = useGlobalStore();

  useEffect(() => {
    setCopy(initialCopy);
    setHeaderText({
      default: initialCopy.navbar.navLinks.find(
        ({ href }) => href === ROUTES.CONTACT
      )?.headerText,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCopy]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isHydrated && typeof window !== 'undefined' && appState?.analytics) {
      appState.analytics.trackPageView(
        window.location.pathname + window.location.search
      );
    }
  }, [isHydrated]);

  useEffect(() => {
    setIsDesktop(width >= breakpointDesktop);
  }, [width]);

  if (!page || isDesktop === null) return null;

  const { emailTile, offices, socialsLabel } = page.contactOfficeCards;

  return (
    <S.Wrapper {...pageMotionProps}>
      {page?.seo && <SEO seo={page.seo} />}
      {isDesktop ? (
        <S.DesktopOfficesContactPage
          emailTile={emailTile}
          offices={offices}
          socialsLabel={socialsLabel}
        />
      ) : (
        <S.MobileOfficesAccordionContactPage
          emailTile={emailTile}
          offices={offices}
        />
      )}
    </S.Wrapper>
  );
};

export default ContactPage;
